import { useEffect, useMemo, useState } from 'react';
import * as React from 'react';
import { debounce } from 'lodash';
import styled from 'styled-components';
import { useCursorPagination } from '@frameio/graphql-client';
import { useGetWorkspaceProjectsQuery } from '@frameio/graphql-client/generated';
import { useProjectSortBy } from '@frameio/shared/hooks';
import { WORKSPACE_PROJECTS_PAGE_SIZE } from '@frameio/shared/resources';
import { DEFAULT_PROJECT_FILTERS } from '@frameio/shared/resources/constants';
import { mapSortByToProjectOrderInputs } from '@frameio/shared/utils';
import { EMPTY_ARRAY } from '@frameio/utils';
import { Box, NavGroup, NavItem, Spinner, theme } from '@frameio/vapor';
import { IconProject } from '@frameio/vapor-icons';
import { useRecentProjects } from '../../../hooks/useRecentProjects';
import type {
  ProjectItemVariant,
  ProjectSelectCallback,
  Workspace,
  WorkspaceProjectSelectCallback,
} from '../types';
import TEXT from './generated/TEXT/WorkspaceGroup';
import { ProjectItem } from './ProjectItem';

const DEFAULT_PAGE_INFO = {
  hasNextPage: false,
};
const REFETCH_DEBOUNCE = 150;
export type WorkspaceGroupProps = {
  onWorkspaceProjectSelect: WorkspaceProjectSelectCallback;
  onLoadingChange: (loading: boolean, workspaceId: string) => void;
  onAvailableProjectCountChange: (count: number, workspaceId?: string) => void;
  workspace: Workspace;
  projectItemVariant?: ProjectItemVariant;
  compact?: boolean;
  activeProjectId?: string;
  loadingProjectId?: string;
  navOnClick?: boolean;
  query?: string | null;
};
export function RecentProjectsGroup({
  onWorkspaceProjectSelect,
  projectItemVariant,
  compact = false,
  activeProjectId,
  loadingProjectId,
}: Omit<
  WorkspaceGroupProps,
  'workspace' | 'onLoadingChange' | 'onAvailableProjectCountChange'
>) {
  const ProjectGroup = compact ? CompactNavGroup : NavGroup;
  const recentProjects = useRecentProjects();
  const handleSelect = (project: any, workspace: any) => {
    onWorkspaceProjectSelect({
      workspace,
      project,
    });
  };

  if (!recentProjects?.length) return null;

  return (
    <ProjectGroup label={TEXT.recent}>
      {recentProjects?.map((project) => (
        <ProjectItem
          key={project?.id}
          isSelected={project?.id === activeProjectId}
          loadingId={loadingProjectId}
          navOnClick={false}
          onSelect={() => handleSelect(project, project?.workspace)}
          project={project as any}
          query={''}
          variant={projectItemVariant}
        />
      ))}
    </ProjectGroup>
  );
}
export function WorkspaceGroup({
  onWorkspaceProjectSelect,
  workspace,
  projectItemVariant,
  onLoadingChange,
  onAvailableProjectCountChange,
  compact = false,
  activeProjectId,
  loadingProjectId,
  navOnClick,
  query = null,
}: WorkspaceGroupProps) {
  const [loadMoreClicked, setLoadMoreClicked] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [sortBy] = useProjectSortBy();
  const { data, fetchNext, loading, refetch } = useCursorPagination(
    useGetWorkspaceProjectsQuery({
      variables: {
        workspaceId: workspace?.id,
        page: {
          first: WORKSPACE_PROJECTS_PAGE_SIZE,
        },
        projectStatusFilters: DEFAULT_PROJECT_FILTERS,
        orders: [mapSortByToProjectOrderInputs(sortBy)],
        query,
      },
      skip: !workspace?.id,
    })
  );

  useEffect(() => {
    if (data) setLoadMoreClicked(false);
    // Only fire when a new completed result arrives (mirrors `onCompleted`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  const debouncedRefetch = useMemo(
    () =>
      debounce(async () => {
        const result = await refetch();
        onAvailableProjectCountChange(
          result?.data?.account?.workspace?.projects?.nodes?.length ?? 0,
          result?.data?.account?.workspace?.id
        );
      }, REFETCH_DEBOUNCE),
    [refetch, onAvailableProjectCountChange]
  );
  useEffect(() => {
    debouncedRefetch();
  }, [debouncedRefetch, query]);
  /**
   * In order to ensure the onLoadingChange
   * callback doesn't run when calling fetchMore,
   * we check whether the inital data has already been retrieved.
   */
  useEffect(() => {
    if (!data && loading) {
      onLoadingChange(true, workspace?.id);
    } else {
      onLoadingChange(false, workspace?.id);
    }
  }, [data, loading, workspace, onLoadingChange]);
  useEffect(() => {
    if (isInitialLoading && !loading && data) {
      setIsInitialLoading(false);
    }
  }, [loading, data, isInitialLoading]);
  if (isInitialLoading || !workspace?.id) return null;
  if (!data?.account?.workspace?.projects?.nodes.length) return null;
  const projects = data?.account?.workspace?.projects?.nodes ?? EMPTY_ARRAY;
  const pageInfo =
    data?.account?.workspace?.projects?.pageInfo ?? DEFAULT_PAGE_INFO;
  const totalCount = data?.account?.workspace?.projects?.totalCount ?? 0;
  const howManyMore = totalCount - (projects?.length ?? 0);
  const handleSelect: ProjectSelectCallback = (project) => {
    onWorkspaceProjectSelect({ workspace, project });
  };
  const handleLoadMoreSelect = () => {
    setLoadMoreClicked(true);
    fetchNext({
      count: NEXT_PAGE_SIZE,
      pageInfo,
    });
  };
  const ProjectGroup = compact ? CompactNavGroup : NavGroup;
  const canShowNavItem =
    howManyMore > 0 &&
    !loadMoreClicked &&
    totalCount > WORKSPACE_PROJECTS_PAGE_SIZE;
  return (
    <ProjectGroup label={workspace.name}>
      {isInitialLoading && (
        <Box style={{ margin: theme.spaces[16] }}>
          <Spinner
            aria-label={TEXT.spinnerAriaLabel}
            centered={true}
            delay={100}
            isLoading={true}
          />
        </Box>
      )}

      {projects?.map((project) => (
        <ProjectItem
          key={project.id}
          isSelected={project.id === activeProjectId}
          loadingId={loadingProjectId}
          navOnClick={navOnClick}
          onSelect={handleSelect}
          project={project}
          query={query ?? ''}
          variant={projectItemVariant}
        />
      ))}

      {canShowNavItem && (
        <NavItem
          iconAfter={
            <div style={{ position: 'relative' }}>
              <Spinner
                aria-label={TEXT.navSpinnerLabel}
                centered={true}
                isLoading={loading}
                size="x-small"
              />
            </div>
          }
          iconBefore={<IconProject />}
          isSelected={false}
          label={TEXT.loadMore(howManyMore)}
          onClick={handleLoadMoreSelect}
          onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key === 'Enter') {
              handleLoadMoreSelect();
            }
          }}
          style={{
            pointerEvents: loading ? 'none' : 'initial',
          }}
          tabIndex={0}
          variant="secondary"
        />
      )}
    </ProjectGroup>
  );
}

const CompactNavGroup = styled(NavGroup)`
  padding-bottom: 0;
`;

const NEXT_PAGE_SIZE = 30;

TEXT.DEFINE({
  /** Button label to load more projects */
  loadMore: (count: number) => `+${count} More`,
  /** Empty state message when workspace has no projects */
  noProjects: 'No projects in this workspace',
  /** Label for recent projects */
  recent: 'Recent',
  /** Aria label for the spinner */
  spinnerAriaLabel: 'Spinner',
  /** Aria label for the navigation loading spinner */
  navSpinnerLabel: 'Loading more workspaces',
});
